import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CareShell } from "../../components/care/CareLayout";
import SearchableSelect from "../../components/care/SearchableSelect";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { compressImageToTarget, useFileUpload } from "../../hooks/useFileUpload";
import { careService } from "../../services/careService";
import { vehicleSelectOptions } from "../../constants/vehicleSelectOptions";
import { convertHeicToJpeg, isHeicFile } from "../../utils/heicConversion";

const initialVehicleForm = {
  year: "",
  make: "",
  model: "",
  trim: "",
  price: "",
  mileage: "",
  condition: "Used",
  vin: "",
  bodyType: "",
  exteriorColor: "",
  interiorColor: "",
  drivetrain: "",
  transmission: "",
  engine: "",
  horsepower: "",
  fuelType: "Gasoline",
  mpgCity: "",
  mpgHighway: "",
  fuelTankSize: "",
  doors: "",
  frontLegroom: "",
  backLegroom: "",
  cargoVolume: "",
  description: "",
  sellerNotes: "",
  status: "Ready",
};

const cleanSelectOptionValue = (value) =>
  String(value || "").replace(/\s*[[(][^\])]*[\])]\s*$/g, "").trim();

const VEHICLE_PHOTO_MAX_BYTES = 100 * 1024;
const VEHICLE_PHOTO_IMAGE_EXTENSIONS = /\.(gif|heic|heif|jpe?g|png|webp)$/i;
const VEHICLE_PHOTO_SAFE_EXTENSIONS = /\.(jpe?g|png)$/i;

const sections = [
  {
    title: "Overview",
    icon: "ti-id",
    fields: [
      ["year", "Year", "select", vehicleSelectOptions.years],
      ["make", "Make", "select", vehicleSelectOptions.makes],
      ["model", "Model", "select", vehicleSelectOptions.models],
      ["trim", "Trim", "select", vehicleSelectOptions.trims],
      ["bodyType", "Body type", "select", vehicleSelectOptions.bodyTypes],
      ["condition", "Condition", "select", vehicleSelectOptions.conditions],
      ["price", "Price", "text"],
      ["mileage", "Mileage (miles)", "number"],
      ["vin", "VIN", "text"],
      ["status", "Inventory status", "select", vehicleSelectOptions.statuses],
    ],
  },
  {
    title: "Colors",
    icon: "ti-photo",
    fields: [
      ["exteriorColor", "Exterior color", "select", vehicleSelectOptions.exteriorColors],
      ["interiorColor", "Interior color", "select", vehicleSelectOptions.interiorColors],
    ],
  },
  {
    title: "Performance",
    icon: "ti-engine",
    fields: [
      ["drivetrain", "Drivetrain", "select", vehicleSelectOptions.drivetrains],
      ["transmission", "Transmission", "select", vehicleSelectOptions.transmissions],
      ["engine", "Engine", "select", vehicleSelectOptions.engines],
      ["horsepower", "Horsepower", "select", vehicleSelectOptions.horsepower],
    ],
  },
  {
    title: "Gas Mileage",
    icon: "ti-gas-station",
    fields: [
      ["fuelType", "Fuel type", "select", vehicleSelectOptions.fuelTypes],
      ["mpgCity", "City gas mileage", "number"],
      ["mpgHighway", "Highway gas mileage", "number"],
      ["fuelTankSize", "Fuel tank size (gal)", "number"],
    ],
  },
  {
    title: "Measurements",
    icon: "ti-ruler-measure",
    fields: [
      ["doors", "Doors", "select", vehicleSelectOptions.doors],
      ["frontLegroom", "Front legroom", "text"],
      ["backLegroom", "Back legroom", "text"],
      ["cargoVolume", "Cargo volume", "text"],
    ],
  },
  {
    title: "Listing Notes",
    icon: "ti-notes",
    fields: [
      ["description", "Public description", "textarea"],
      ["sellerNotes", "Internal seller notes", "textarea"],
    ],
  },
];

const defaultSafetyFeatures = [
  "ABS Brakes",
  "Backup Camera",
  "Blind Spot Monitoring",
  "Curtain Airbags",
  "Driver Airbag",
  "Front Side Airbags",
  "Passenger Airbag",
];

const selectableSafetyFeatures = [
  "ABS Brakes",
  "Backup Camera",
  "Blind Spot Monitoring",
  "Curtain Airbags",
  "Driver Airbag",
  "Front Side Airbags",
  "Passenger Airbag",
  "Lane Departure Warning",
  "Lane Keep Assist",
  "Forward Collision Warning",
  "Automatic Emergency Braking",
  "Rear Cross Traffic Alert",
  "Parking Sensors",
  "Traction Control",
  "Stability Control",
  "Adaptive Cruise Control",
];

const defaultOptions = [
  "Appearance Package",
  "Bluetooth",
  "Alloy Wheels",
  "Tow Package",
  "Remote Start",
  "Steel Wheels",
];

const selectableOptions = [
  "Appearance Package",
  "Backup Camera",
  "Bluetooth",
  "Navigation System",
  "Apple CarPlay",
  "Android Auto",
  "Alloy Wheels",
  "Steel Wheels",
  "Tow Package",
  "Remote Start",
  "Heated Seats",
  "Leather Seats",
  "Sunroof / Moonroof",
  "Keyless Entry",
  "Premium Sound",
  "Third Row Seating",
  "Power Liftgate",
  "Bed Liner",
  "Running Boards",
  "Adaptive Cruise Control",
];

const mileageDisplay = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return /\bmi\b|\bmile/i.test(text) ? text : `${text} miles`;
};

const mileageInputValue = (value) => String(value || "").replace(/\s*(miles?|mi)\b/gi, "").trim();
const firstText = (...values) => values.find((value) => value !== null && value !== undefined && value !== "") || "";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isTemporarySignedUrl = (value = "") =>
  /^https:\/\/storage\.googleapis\.com\//i.test(value) && /(?:Signature=|X-Goog-Signature=)/.test(value);
const fileIdFromSignedUrl = (value = "") => {
  try {
    const url = new URL(value);
    const pathUuid = decodeURIComponent(url.pathname)
      .split("/")
      .reverse()
      .map((segment) => segment.replace(/\.[^.]+$/, ""))
      .find((segment) => UUID_PATTERN.test(segment));
    return pathUuid || "";
  } catch {
    return "";
  }
};
const photoUrl = (value) => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return firstText(value.signedUrl, value.url, value.photoUrl, value.imageUrl, value.downloadUrl, value.fileUrl, value.publicUrl);
};
const photoReferenceFromValue = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (isTemporarySignedUrl(trimmed)) return fileIdFromSignedUrl(trimmed);
    return trimmed;
  }
  if (!value || typeof value !== "object") return "";
  const fileId = firstText(value.photoRef, value.ref, value.reference, value.fileId, value.fid, value.photoId, value.imageId, value.id);
  if (fileId) return fileId;
  const url = photoUrl(value);
  if (!url) return "";
  return isTemporarySignedUrl(url) ? fileIdFromSignedUrl(url) : url;
};
const photoUploadFromValue = (value, index) => {
  const url = photoUrl(value);
  const photoRef = photoReferenceFromValue(value);
  if (!url && !photoRef) return null;
  const fileId = typeof value === "object" ? firstText(value.photoRef, value.ref, value.reference, value.fileId, value.fid, value.photoId, value.imageId, value.id) : "";
  return {
    id: photoRef || fileId || `${url}-${index}`,
    fileId,
    photoRef,
    fileName: typeof value === "object" ? firstText(value.fileName, value.name, `Photo ${index + 1}`) : `Photo ${index + 1}`,
    signedUrl: url,
    status: "Uploaded",
    uploadedAt: typeof value === "object" ? value.uploadedAt : "",
  };
};
const photoUploadsFromVehicle = (vehicle = {}) => {
  const refs = Array.isArray(vehicle.photoRefs) ? vehicle.photoRefs.filter(Boolean) : [];
  const urls = [...new Set([
    vehicle.mainPhoto,
    ...(Array.isArray(vehicle.photos) ? vehicle.photos : []),
  ].filter(Boolean))];

  if (refs.length) {
    return refs
      .map((ref, index) => photoUploadFromValue({
        photoRef: ref,
        signedUrl: urls[index] || "",
        fileName: `Photo ${index + 1}`,
      }, index))
      .filter(Boolean);
  }

  return [
    photoUploadFromValue(vehicle.mainPhoto, 0),
    ...(Array.isArray(vehicle.photos) ? vehicle.photos.map((photo, index) => photoUploadFromValue(photo, index + 1)) : []),
  ].filter(Boolean);
};
const isVehiclePhotoFile = (file = {}) =>
  String(file.type || "").startsWith("image/") || VEHICLE_PHOTO_IMAGE_EXTENSIONS.test(String(file.name || ""));
const jpegNameFor = (fileName = "vehicle-photo") => {
  const withoutExtension = fileName.replace(/\.[^.]+$/, "") || "vehicle-photo";
  return `${withoutExtension}.jpg`;
};
const fileFromBlob = (blob, sourceFile, contentType) => new File(
  [blob],
  contentType === "image/png" ? sourceFile.name : jpegNameFor(sourceFile.name),
  {
    lastModified: sourceFile.lastModified || Date.now(),
    type: contentType,
  },
);
const optimizeVehiclePhoto = async (file) => {
  const normalizedFile = isHeicFile(file) ? await convertHeicToJpeg(file) : file;
  const normalizedType = String(normalizedFile.type || "").toLowerCase();
  const isSafeOriginalType = normalizedType === "image/png" || normalizedType === "image/jpeg";
  const hasSafeOriginalExtension = VEHICLE_PHOTO_SAFE_EXTENSIONS.test(String(normalizedFile.name || ""));
  const shouldForceJpeg = normalizedFile.size > VEHICLE_PHOTO_MAX_BYTES || !isSafeOriginalType || !hasSafeOriginalExtension;

  if (!shouldForceJpeg && normalizedFile.size <= VEHICLE_PHOTO_MAX_BYTES) {
    return normalizedFile;
  }

  const compressed = await compressImageToTarget(normalizedFile, {
    allowOriginalFallback: false,
    forceJpeg: shouldForceJpeg,
    initialQuality: 0.82,
    maxBytes: VEHICLE_PHOTO_MAX_BYTES,
    maxHeight: 1600,
    maxWidth: 1600,
    minHeight: 120,
    minQuality: 0.18,
    minWidth: 120,
    scaleStep: 0.85,
    stepQuality: 0.08,
  });
  const contentType = shouldForceJpeg ? "image/jpeg" : compressed.contentType || normalizedFile.type || "image/png";
  if (compressed.size > VEHICLE_PHOTO_MAX_BYTES) {
    throw new Error(`Could not optimize ${file.name} below 100 KB.`);
  }
  return fileFromBlob(compressed.blob, normalizedFile, contentType);
};
const formDataFromVehicle = (vehicle = {}) => ({
  ...initialVehicleForm,
  year: firstText(vehicle.year),
  make: cleanSelectOptionValue(firstText(vehicle.make)),
  model: cleanSelectOptionValue(firstText(vehicle.model)),
  trim: cleanSelectOptionValue(firstText(vehicle.trim)),
  price: firstText(vehicle.price, vehicle.askingPrice, vehicle.listPrice),
  mileage: mileageInputValue(firstText(vehicle.mileage, vehicle.odometer)),
  condition: cleanSelectOptionValue(firstText(vehicle.condition, initialVehicleForm.condition)),
  vin: firstText(vehicle.vin),
  bodyType: cleanSelectOptionValue(firstText(vehicle.bodyType)),
  exteriorColor: cleanSelectOptionValue(firstText(vehicle.exteriorColor, vehicle.color)),
  interiorColor: cleanSelectOptionValue(firstText(vehicle.interiorColor)),
  drivetrain: cleanSelectOptionValue(firstText(vehicle.drivetrain, vehicle.driveType)),
  transmission: cleanSelectOptionValue(firstText(vehicle.transmission)),
  engine: cleanSelectOptionValue(firstText(vehicle.engine)),
  horsepower: firstText(vehicle.horsepower),
  fuelType: cleanSelectOptionValue(firstText(vehicle.fuelType, vehicle.fuel, initialVehicleForm.fuelType)),
  mpgCity: firstText(vehicle.mpgCity, vehicle.cityMpg),
  mpgHighway: firstText(vehicle.mpgHighway, vehicle.highwayMpg),
  fuelTankSize: firstText(vehicle.fuelTankSize),
  doors: firstText(vehicle.doors),
  frontLegroom: firstText(vehicle.frontLegroom),
  backLegroom: firstText(vehicle.backLegroom),
  cargoVolume: firstText(vehicle.cargoVolume),
  description: firstText(vehicle.description),
  sellerNotes: firstText(vehicle.sellerNotes),
  status: cleanSelectOptionValue(firstText(vehicle.status, vehicle.inventoryStatus, initialVehicleForm.status)),
});

function VehicleFormField({ field, value, onChange }) {
  const [name, label, type, options = []] = field;
  return (
    <label className={`vehicle-capture-field${type === "textarea" ? " full-span" : ""}`}>
      <span>{label}</span>
      {type === "select" ? (
        <SearchableSelect
          ariaLabel={label}
          className="vehicle-capture-searchable-select"
          customPlaceholder={`Specify ${label.toLowerCase()}`}
          name={name}
          onChange={(nextValue) => onChange(name, nextValue)}
          options={options}
          placeholder="Select"
          value={value || ""}
        />
      ) : type === "textarea" ? (
        <textarea
          name={name}
          rows={4}
          value={value || ""}
          onChange={(event) => onChange(name, event.target.value)}
        />
      ) : (
        <input name={name} type={type} value={value || ""} onChange={(event) => onChange(name, event.target.value)} />
      )}
    </label>
  );
}

const valueWithUnit = (value, unit) => {
  const text = String(value || "").trim();
  return text ? `${text} ${unit}` : "";
};
const optionalField = (value) => {
  const text = String(value || "").trim();
  return text || undefined;
};
const optionalDetailRows = (rows) =>
  rows.filter(([, value]) => String(value || "").trim());

export default function VehicleNewPage() {
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { uploadFile, getFileSignedUrl, uploading: photoUploading } = useFileUpload();
  const isEditMode = Boolean(vehicleId);
  const [formData, setFormData] = useState(initialVehicleForm);
  const [photoUploads, setPhotoUploads] = useState([]);
  const [selectedSafetyFeatures, setSelectedSafetyFeatures] = useState(defaultSafetyFeatures);
  const [customSafetyFeature, setCustomSafetyFeature] = useState("");
  const [selectedOptions, setSelectedOptions] = useState(defaultOptions);
  const [customOption, setCustomOption] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(isEditMode);
  const [draggedPhotoId, setDraggedPhotoId] = useState("");
  const actorId = user?.id || user?.userId || user?.uid || "";
  const workspaceId = selectedWorkspaceId || selectedWorkspace?.id || "";

  const vehicleTitle = useMemo(() =>
    [formData.year, formData.make, formData.model, formData.trim].filter(Boolean).join(" ") || "New vehicle listing",
  [formData.year, formData.make, formData.model, formData.trim]);

  useEffect(() => {
    if (!vehicleId) return undefined;
    let isMounted = true;
    setLoadingVehicle(true);
    careService.vehicles.get(vehicleId)
      .then((vehicle) => {
        if (!isMounted) return;
        setFormData(formDataFromVehicle(vehicle || {}));
        const photos = photoUploadsFromVehicle(vehicle);
        setPhotoUploads([...new Map(photos.map((photo) => [photo.photoRef || photo.fileId || photo.signedUrl || photo.id, photo])).values()]);
        setSelectedSafetyFeatures(Array.isArray(vehicle?.safetyFeatures) ? vehicle.safetyFeatures : defaultSafetyFeatures);
        setSelectedOptions(Array.isArray(vehicle?.options) ? vehicle.options : defaultOptions);
        setSavedMessage("");
        setSubmitError("");
      })
      .catch((error) => {
        if (!isMounted) return;
        setSubmitError(error?.response?.data?.message || error?.message || "Unable to load vehicle.");
      })
      .finally(() => {
        if (isMounted) setLoadingVehicle(false);
      });
    return () => {
      isMounted = false;
    };
  }, [vehicleId]);

  const updateField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setSavedMessage("");
    setSubmitError("");
  };

  const toggleOption = (option) => {
    setSelectedOptions((current) =>
      current.includes(option) ? current.filter((item) => item !== option) : [...current, option],
    );
    setSavedMessage("");
    setSubmitError("");
  };

  const toggleSafetyFeature = (feature) => {
    setSelectedSafetyFeatures((current) =>
      current.includes(feature) ? current.filter((item) => item !== feature) : [...current, feature],
    );
    setSavedMessage("");
    setSubmitError("");
  };

  const addCustomSafetyFeature = () => {
    const feature = customSafetyFeature.trim();
    if (!feature) return;
    setSelectedSafetyFeatures((current) => current.includes(feature) ? current : [...current, feature]);
    setCustomSafetyFeature("");
    setSavedMessage("");
    setSubmitError("");
  };

  const addCustomOption = () => {
    const option = customOption.trim();
    if (!option) return;
    setSelectedOptions((current) => current.includes(option) ? current : [...current, option]);
    setCustomOption("");
    setSavedMessage("");
    setSubmitError("");
  };

  const addPhotoUploads = async (event) => {
    const files = Array.from(event.target.files || []).filter(isVehiclePhotoFile);
    event.target.value = "";
    if (!files.length) return;

    const pendingPhotos = files.map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
      fileName: file.name,
      size: file.size,
      type: file.type,
      status: "Optimizing",
    }));
    setPhotoUploads((current) => [...current, ...pendingPhotos]);
    setSavedMessage("");
    setSubmitError("");

    for (const photo of pendingPhotos) {
      const file = files.find((candidate) => candidate.name === photo.fileName && candidate.size === photo.size);
      try {
        const optimizedFile = await optimizeVehiclePhoto(file);
        setPhotoUploads((current) => current.map((item) =>
          item.id === photo.id
            ? {
              ...item,
              fileName: optimizedFile.name,
              optimizedSize: optimizedFile.size,
              originalSize: file.size,
              type: optimizedFile.type,
              status: "Uploading",
            }
            : item,
        ));
        const upload = await uploadFile(null, optimizedFile, {
          confirmUpload: true,
          fileType: "PHOTO",
          includeUserId: false,
          preserveOriginal: true,
          purpose: "OTHER",
          skipUserValidation: true,
        });
        const fileId = upload?.fileId || upload?.file?.id || "";
        const signedUrl = fileId ? await getFileSignedUrl(fileId).catch(() => "") : "";
        setPhotoUploads((current) => current.map((item) =>
          item.id === photo.id
            ? {
              ...item,
              fileId,
              photoRef: fileId,
              confirmed: Boolean(upload?.confirmed),
              signedUrl,
              status: upload?.confirmed
                ? `Uploaded (${Math.ceil((optimizedFile.size || 0) / 1024)} KB)`
                : `Uploaded, pending confirmation (${Math.ceil((optimizedFile.size || 0) / 1024)} KB)`,
              uploadedAt: new Date().toISOString(),
            }
            : item,
        ));
      } catch (error) {
        setPhotoUploads((current) => current.map((item) =>
          item.id === photo.id
            ? { ...item, error: error?.message || "Upload failed", status: "Upload failed" }
            : item,
        ));
      }
    }
  };

  const removePhotoUpload = (photoId) => {
    setPhotoUploads((current) => current.filter((photo) => photo.id !== photoId));
    setSavedMessage("");
    setSubmitError("");
  };

  const movePhotoUpload = (photoIndex, direction) => {
    setPhotoUploads((current) => {
      const fromIndex = Number(photoIndex);
      const toIndex = fromIndex + direction;
      if (!Number.isInteger(fromIndex) || fromIndex < 0 || toIndex < 0 || toIndex >= current.length) return current;
      const next = [...current];
      [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
      return next;
    });
    setSavedMessage("");
    setSubmitError("");
  };

  const reorderPhotoUploads = (targetPhotoId) => {
    if (!draggedPhotoId || draggedPhotoId === targetPhotoId) return;
    setPhotoUploads((current) => {
      const fromIndex = current.findIndex((photo) => photo.id === draggedPhotoId);
      const toIndex = current.findIndex((photo) => photo.id === targetPhotoId);
      if (fromIndex < 0 || toIndex < 0) return current;
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setSavedMessage("");
    setSubmitError("");
  };

  const buildVehicleRequest = () => ({
    workspaceId: workspaceId || null,
    year: formData.year,
    make: formData.make,
    model: formData.model,
    trim: formData.trim,
    bodyType: formData.bodyType,
    condition: formData.condition,
    price: formData.price,
    mileage: mileageDisplay(formData.mileage),
    vin: formData.vin,
    status: formData.status,
    exteriorColor: formData.exteriorColor,
    interiorColor: formData.interiorColor,
    drivetrain: formData.drivetrain,
    transmission: formData.transmission,
    engine: formData.engine,
    horsepower: formData.horsepower,
    fuelType: formData.fuelType,
    mpgCity: optionalField(formData.mpgCity),
    mpgHighway: optionalField(formData.mpgHighway),
    fuelTankSize: optionalField(formData.fuelTankSize),
    doors: formData.doors,
    frontLegroom: formData.frontLegroom,
    backLegroom: formData.backLegroom,
    cargoVolume: formData.cargoVolume,
    description: formData.description,
    sellerNotes: formData.sellerNotes,
    photos: photoUploads
      .map((photo) => photo.photoRef || photo.fileId || "")
      .filter(Boolean),
    safetyFeatures: selectedSafetyFeatures,
    options: selectedOptions,
    listingDetails: {
      features: optionalDetailRows([
        ["Mileage", mileageDisplay(formData.mileage), "ti-dashboard"],
        ["Drivetrain", formData.drivetrain, "ti-route"],
        ["Exterior color", formData.exteriorColor, "ti-droplet"],
        ["Interior color", formData.interiorColor, "ti-armchair"],
        ["City gas mileage", valueWithUnit(formData.mpgCity, "MPG"), "ti-gas-station"],
        ["Engine", formData.engine, "ti-engine"],
        ["Fuel type", formData.fuelType, "ti-gas-station"],
        ["Transmission", formData.transmission, "ti-manual-gearbox"],
      ]),
      overview: optionalDetailRows([
        ["Make", formData.make],
        ["Model", formData.model],
        ["Year", formData.year],
        ["Trim", formData.trim],
        ["Body type", formData.bodyType],
        ["Exterior color", formData.exteriorColor],
        ["Interior color", formData.interiorColor],
        ["Mileage", mileageDisplay(formData.mileage)],
        ["Condition", formData.condition],
        ["VIN", formData.vin],
      ]),
      fuelEconomy: optionalDetailRows([
        ["Fuel tank size", valueWithUnit(formData.fuelTankSize, "gal")],
        ["Highway gas mileage", valueWithUnit(formData.mpgHighway, "MPG")],
        ["Fuel type", formData.fuelType],
        ["City gas mileage", valueWithUnit(formData.mpgCity, "MPG")],
      ]),
      performance: optionalDetailRows([
        ["Transmission", formData.transmission],
        ["Engine", formData.engine],
        ["Drivetrain", formData.drivetrain],
        ["Horsepower", formData.horsepower],
      ]),
      safetyRatings: [],
      safetyFeatures: selectedSafetyFeatures,
      measurements: optionalDetailRows([
        ["Doors", formData.doors],
        ["Back legroom", formData.backLegroom],
        ["Front legroom", formData.frontLegroom],
        ["Cargo volume", formData.cargoVolume],
      ]),
      options: selectedOptions,
    },
  });

  const saveLocalDraft = () => {
    const draft = {
      title: vehicleTitle,
      ...buildVehicleRequest(),
    };
    localStorage.setItem(isEditMode ? `white-eagle-vehicle-update-draft-${vehicleId}` : "white-eagle-new-vehicle-draft", JSON.stringify(draft));
    setSavedMessage(`${vehicleTitle} ${isEditMode ? "update draft" : "draft"} saved locally.`);
    setSubmitError("");
  };

  const submitVehicle = async (event) => {
    event.preventDefault();
    setSavedMessage("");
    setSubmitError("");
    if (!workspaceId) {
      setSubmitError("Select a workspace before submitting this vehicle.");
      return;
    }
    setSubmitting(true);
    try {
      const request = buildVehicleRequest();
      if (isEditMode) {
        const updated = await careService.vehicles.update(vehicleId, request, {
          actorId,
          workspaceId,
          wid: workspaceId,
        });
        localStorage.removeItem(`white-eagle-vehicle-update-draft-${vehicleId}`);
        setSavedMessage(`${updated?.title || vehicleTitle} updated.`);
        navigate(`/vehicles/${vehicleId}`);
      } else {
        const created = await careService.vehicles.create(request, {
          actorId,
          workspaceId,
          wid: workspaceId,
        });
        localStorage.removeItem("white-eagle-new-vehicle-draft");
        const createdId = created?.id || created?.vehicleId;
        setSavedMessage(`${created?.title || vehicleTitle} submitted to inventory.`);
        if (createdId) {
          navigate(`/vehicles/${createdId}`);
        }
      }
    } catch (error) {
      setSubmitError(error?.response?.data?.message || error?.message || `Unable to ${isEditMode ? "update" : "submit"} vehicle.`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialVehicleForm);
    setPhotoUploads([]);
    setSelectedSafetyFeatures(defaultSafetyFeatures);
    setCustomSafetyFeature("");
    setSelectedOptions(defaultOptions);
    setCustomOption("");
    setSavedMessage("");
    setSubmitError("");
    setSubmitting(false);
  };

  return (
    <CareShell className="dashboard-content vehicle-capture-page">
      <div className="crumbs">Vehicles <i className="ti ti-chevron-right" /> {isEditMode ? `Update ${vehicleTitle}` : "Add Vehicle"}</div>
      <section className="vehicle-capture-hero">
        <div>
          <span className="section-eyebrow">Inventory</span>
          <h1>{isEditMode ? "Update vehicle details" : "Add vehicle details"}</h1>
          <p>{isEditMode ? "Review and update the complete listing information for this vehicle." : "Capture the complete listing information needed for inventory cards and the vehicle detail page."}</p>
        </div>
        <div className="vehicle-capture-hero-actions">
          <Link className="mini-btn" to={isEditMode ? `/vehicles/${vehicleId}` : "/vehicles"}><i className="ti ti-arrow-left" /> {isEditMode ? "Vehicle Details" : "Inventory"}</Link>
          <button className="mini-btn danger" type="button" onClick={resetForm}><i className="ti ti-refresh" /> Reset</button>
        </div>
      </section>

      <form className="vehicle-capture-layout" onSubmit={submitVehicle}>
        <aside className="vehicle-capture-summary">
          <span className="round-icon blue"><i className="ti ti-car" /></span>
          <h2>{loadingVehicle ? "Loading vehicle..." : vehicleTitle}</h2>
          <p>{[formData.price, mileageDisplay(formData.mileage), formData.drivetrain].filter(Boolean).join(" | ") || "Complete the form to build the listing."}</p>
          <button className="btn primary full" type="submit" disabled={submitting || photoUploading || loadingVehicle}>
            <i className="ti ti-device-floppy" /> {submitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update Vehicle" : "Submit Vehicle")}
          </button>
          <button className="mini-btn full" type="button" onClick={saveLocalDraft} disabled={submitting || loadingVehicle}>
            <i className="ti ti-file-pencil" /> Save Draft
          </button>
          {savedMessage ? <strong className="vehicle-capture-saved">{savedMessage}</strong> : null}
          {submitError ? <strong className="vehicle-capture-error">{submitError}</strong> : null}
        </aside>

        <div className="vehicle-capture-sections">
          {sections.map((section) => (
            <section className="vehicle-capture-card" key={section.title}>
              <div className="vehicle-capture-card-head">
                <i className={`ti ${section.icon}`} />
                <h2>{section.title}</h2>
              </div>
              <div className="vehicle-capture-grid">
                {section.fields.map((field) => (
                  <VehicleFormField field={field} value={formData[field[0]]} onChange={updateField} key={field[0]} />
                ))}
              </div>
            </section>
          ))}

          <section className="vehicle-capture-card vehicle-photo-upload-card">
            <div className="vehicle-capture-card-head">
              <i className="ti ti-camera-plus" />
              <h2>Photos</h2>
            </div>
            <label className="vehicle-photo-upload-dropzone">
              <input accept="image/*,.heic,.heif,image/heic,image/heif" multiple onChange={addPhotoUploads} type="file" />
              <i className="ti ti-cloud-upload" />
              <strong>{photoUploading ? "Uploading photos..." : "Upload vehicle photos"}</strong>
              <small>JPG, PNG, WEBP, HEIC, or GIF. The first uploaded photo will be used as the main listing photo.</small>
            </label>
            {photoUploads.length ? (
              <div className="vehicle-photo-upload-grid">
                {photoUploads.map((photo, index) => (
                  <article
                    className={`vehicle-photo-upload-item${draggedPhotoId === photo.id ? " is-dragging" : ""}`}
                    draggable
                    key={photo.id}
                    onDragStart={(event) => {
                      setDraggedPhotoId(photo.id);
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", photo.id);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      reorderPhotoUploads(photo.id);
                    }}
                    onDragEnd={() => setDraggedPhotoId("")}
                  >
                    {photo.signedUrl ? <img src={photo.signedUrl} alt={photo.fileName || `Vehicle photo ${index + 1}`} /> : (
                      <span className="vehicle-photo-upload-placeholder"><i className="ti ti-photo-up" /></span>
                    )}
                    <div>
                      <strong>{index === 0 ? "Main photo" : `Photo ${index + 1}`}</strong>
                      <span>{photo.fileName}</span>
                      <small className={photo.error ? "is-error" : ""}>{photo.error || photo.status}</small>
                    </div>
                    <div
                      className="vehicle-photo-upload-controls"
                      aria-label={`Reorder ${photo.fileName || `photo ${index + 1}`}`}
                      onMouseDown={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      onDragStart={(event) => event.preventDefault()}
                    >
                      <button
                        aria-label={`Move ${photo.fileName || `photo ${index + 1}`} earlier`}
                        disabled={index === 0}
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          movePhotoUpload(index, -1);
                        }}
                      >
                        <i className="ti ti-chevron-up" />
                      </button>
                      <button
                        aria-label={`Move ${photo.fileName || `photo ${index + 1}`} later`}
                        disabled={index === photoUploads.length - 1}
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          movePhotoUpload(index, 1);
                        }}
                      >
                        <i className="ti ti-chevron-down" />
                      </button>
                    </div>
                    <button className="vehicle-photo-upload-remove" aria-label={`Remove ${photo.fileName}`} type="button" onClick={() => removePhotoUpload(photo.id)}>
                      <i className="ti ti-x" />
                    </button>
                  </article>
                ))}
              </div>
            ) : null}
          </section>

          <section className="vehicle-capture-card">
            <div className="vehicle-capture-card-head">
              <i className="ti ti-shield" />
              <h2>Safety Features</h2>
            </div>
            <div className="vehicle-option-picker" aria-label="Safety features">
              {selectableSafetyFeatures.map((feature) => (
                <button
                  className={selectedSafetyFeatures.includes(feature) ? "is-selected" : ""}
                  key={feature}
                  onClick={() => toggleSafetyFeature(feature)}
                  type="button"
                >
                  <i className={`ti ${selectedSafetyFeatures.includes(feature) ? "ti-check" : "ti-plus"}`} />
                  {feature}
                </button>
              ))}
            </div>
            <div className="vehicle-custom-option">
              <label className="vehicle-capture-field">
                <span>Custom safety feature</span>
                <input value={customSafetyFeature} onChange={(event) => setCustomSafetyFeature(event.target.value)} onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addCustomSafetyFeature();
                  }
                }} />
              </label>
              <button className="mini-btn" type="button" onClick={addCustomSafetyFeature}><i className="ti ti-plus" /> Add Feature</button>
            </div>
            {selectedSafetyFeatures.length ? (
              <div className="vehicle-selected-options">
                {selectedSafetyFeatures.map((feature) => (
                  <button key={feature} type="button" onClick={() => toggleSafetyFeature(feature)}>
                    {feature} <i className="ti ti-x" />
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="vehicle-capture-card">
            <div className="vehicle-capture-card-head">
              <i className="ti ti-list-check" />
              <h2>Options</h2>
            </div>
            <div className="vehicle-option-picker" aria-label="Vehicle options">
              {selectableOptions.map((option) => (
                <button
                  className={selectedOptions.includes(option) ? "is-selected" : ""}
                  key={option}
                  onClick={() => toggleOption(option)}
                  type="button"
                >
                  <i className={`ti ${selectedOptions.includes(option) ? "ti-check" : "ti-plus"}`} />
                  {option}
                </button>
              ))}
            </div>
            <div className="vehicle-custom-option">
              <label className="vehicle-capture-field">
                <span>Custom option</span>
                <input value={customOption} onChange={(event) => setCustomOption(event.target.value)} onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addCustomOption();
                  }
                }} />
              </label>
              <button className="mini-btn" type="button" onClick={addCustomOption}><i className="ti ti-plus" /> Add Option</button>
            </div>
            {selectedOptions.length ? (
              <div className="vehicle-selected-options">
                {selectedOptions.map((option) => (
                  <button key={option} type="button" onClick={() => toggleOption(option)}>
                    {option} <i className="ti ti-x" />
                  </button>
                ))}
              </div>
            ) : null}
          </section>
        </div>
      </form>
    </CareShell>
  );
}
